import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  TextField,
  Grid,
  Typography,
  FormControlLabel,
  Divider,
  Box,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Button,
  MenuItem,
  IconButton,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import { api_path } from "../../shared.js";
import useThaiAddress from "./useThaiAddress";
import ThaiAddressSection from "./ThaiAddressSection";

// ─────────────────────────────────────────────────────────────────
//  Reusable TextField wrapper (ไม่เปลี่ยน)
// ─────────────────────────────────────────────────────────────────
const F = React.memo(({
  name, label, required, xs = 6, type, multiline, rows,
  inputProps, onChange, disabled, value, error, helperText, fieldRef,
}) => (
  <Grid item xs={xs} ref={fieldRef}>
    <TextField
      label={label + (required ? " *" : "")}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      fullWidth
      type={type}
      multiline={multiline}
      rows={rows}
      InputLabelProps={type === "date" || type === "number" ? { shrink: true } : undefined}
      error={!!error}
      helperText={helperText}
      inputProps={inputProps}
      disabled={disabled}
    />
  </Grid>
));

// ─────────────────────────────────────────────────────────────────
//  Form State Hook (ไม่เปลี่ยน)
// ─────────────────────────────────────────────────────────────────
function useCustomerForm() {
  const [form, setForm] = useState({
    requestDate: new Date().toISOString().split("T")[0],
    customerCode: "",
    requestType: "new",
    oldCustomerCode: "",
    paymentType: "cash",
    channel: "PP",
    agentName: "",
    otherType: "",
    nameTH: "",
    nameEN: "",
    address1: "",
    address2: "",
    address3: "",
    address4: "",
    district: "",   // Amphoe (เขต/อำเภอ)
    subdistrict: "",   // Tambon (แขวง/ตำบล)
    city: "",   // Province (จังหวัด)
    postalCode: "",
    country: "Thailand",
    taxClassification: "",
    tel: "",
    ext: "",
    fax: "",
    mobile: "",
    email: "",
    contactPerson: "",
    contactFinance: "",
    financeTel: "",
    financeExt: "",
    taxId: "",
    businessType: "",
    registerDate: "",
    registeredCapital: "",
    paidUpCapital: "",
    branch: "",
    reportYear: "",
    netSales: "",
    netProfit: "",
    fixedAssets: "",
    currentAssets: "",
    totalLiabilities: "",
    shareholderEquity: "",
    creditMonthlySales: "",
    creditLimitRequest: "",
    creditTermRequest: "",
    currency: "THB",
    creditCountFrom: "invoice_date",
    paymentMethod: "cheque",
    paymentDay: "",
    approval_status: 0, // ✅ ใช้เช็ค permission การกรอก customerCode
  });

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  return { form, setForm, handleChange };
}

// ─────────────────────────────────────────────────────────────────
//  Main Component
// ─────────────────────────────────────────────────────────────────
export default function CustomerGeneralForm() {
  const [searchParams] = useSearchParams();
  const job_id = searchParams.get("id");
  const [isDuplicateTax, setIsDuplicateTax] = useState(false);
  const [isDuplicateName, setIsDuplicateName] = useState(false);
  const [duplicateNameList, setDuplicateNameList] = useState([]);

  const [duplicateTaxList, setDuplicateTaxList] = useState([]);

  const { form, setForm, handleChange } = useCustomerForm();
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState({
    doc_company_cert: [],
    doc_financial_statement: [],
    doc_vat20: [],
    doc_delivery_schedule: [],
    doc_company_map: [],
    doc_company_photo: [],
  });

  const fieldRefs = useRef({});
  const debounceTimer = useRef(null);
  const debounceNameTimer = useRef(null);
  // ✅ state สำหรับเก็บตัวเลือก Business Type ที่ดึงจาก API
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [loadingBusinessType, setLoadingBusinessType] = useState(true);
  useEffect(() => {
    const fetchBusinessType = async () => {
      try {
        const res = await fetch(`${api_path}/MS_CUSTOMER_MASTER.php?type=INDUSTRY`);
        const result = await res.json();
        const list = Array.isArray(result) ? result : result.data || [];
        setBusinessTypeOptions(list);
      } catch (error) {
        console.error("โหลด Business Type ไม่สำเร็จ:", error);
      } finally {
        setLoadingBusinessType(false);
      }
    };
    fetchBusinessType();
  }, []);
  const makeRef = useCallback((name) => (el) => {
    fieldRefs.current[name] = el;
  }, []);

  // ── Thai Address cascade hook ──
  const {
    provinces,
    amphoes,
    tambons,
    handleProvinceChange,
    handleAmphoeChange,
    handleTambonChange,
  } = useThaiAddress(form, setForm);

  // ── Fetch existing customer ──
  useEffect(() => {
    if (job_id) fetchCustomer(job_id);
  }, [job_id]);

  const fetchCustomer = async (id) => {
    try {
      const res = await fetch(`${api_path}/customer/get-CustomerRegistration.php?id=${id}`);
      const result = await res.json();
      if (!result || !result.customer) {
        console.warn("ไม่พบข้อมูล customer:", result);
        return;
      }
      const data = result.customer;
      const files = result.files;

      setForm((prev) => ({
        ...prev,
        customerCode: data.CustomerCode || "",
        approval_status: Number(data.approval_status) || 0,
        nameTH: data.CustomerNameTH || "",
        nameEN: data.CustomerNameEN || "",
        branch: data.CustomerBranch || "",
        address1: data.CustomerAddress1 || "",
        address2: data.CustomerAddress2 || "",
        address3: data.CustomerAddress3 || "",
        address4: data.CustomerAddress4 || "",
        subdistrict: data.CustomerSubDistrict || "",
        district: data.CustomerDistrict || "",
        city: data.CustomerCity || "",
        postalCode: data.CustomerPostCode || "",
        country: data.Country || "Thailand",
        taxClassification: data.TaxClassification || "",
        tel: data.CustomerTel || "",
        ext: data.CustomerExt || "",
        fax: data.CustomerFax || "",
        mobile: data.CustomerMobile || "",
        email: data.CustomerEmail || "",
        contactPerson: data.ContactPerson || "",
        contactFinance: data.FinanceContact || "",
        taxId: data.TaxID || "",
        businessType: data.BusinessType || "",
        registerDate: data.RegistrationDate || "",
        registeredCapital: data.RegisteredCapital || "",
        paidUpCapital: data.PaidUpCapital || "",
        reportYear: data.ReportYear || "",
        netSales: data.NetSales || "",
        netProfit: data.NetProfit || "",
        fixedAssets: data.FixedAssets || "",
        currentAssets: data.CurrentAssets || "",
        totalLiabilities: data.TotalLiabilities || "",
        shareholderEquity: data.ShareholderEquity || "",
        creditMonthlySales: data.CreditMonthlySales || "",
        creditLimitRequest: data.CreditLimit || "",
        creditTermRequest: data.CreditDay || "",
        creditCountFrom: data.CreditCountFrom || "invoice_date",
        paymentMethod: data.PaymentMethod || "cheque",
        paymentDay: data.PaymentDay || "",
      }));
      const mapFiles = (arr) =>
        (arr || []).map((f) => ({ ...f, name: f.file_name }));
      setFiles({
        doc_company_cert: mapFiles(files?.doc_company_cert),
        doc_financial_statement: mapFiles(files?.doc_financial_statement),
        doc_vat20: mapFiles(files?.doc_vat20),
        doc_delivery_schedule: mapFiles(files?.doc_delivery_schedule),
        doc_company_map: mapFiles(files?.doc_company_map),
        doc_company_photo: mapFiles(files?.doc_company_photo),
      });
    } catch (error) {
      console.error("โหลดข้อมูลไม่สำเร็จ", error);
    }
  };

  // ── Tax ID ──
  const handleChangeTaxid = useCallback((e) => {
    const taxid = e.target.value;
    if (!/^\d*$/.test(taxid)) return;
    setForm((prev) => ({ ...prev, taxId: taxid }));
    clearTimeout(debounceTimer.current);
    if (taxid.length > 3) {
      debounceTimer.current = setTimeout(() => checkDuplicateTax(taxid), 500);
    } else {
      setIsDuplicateTax(false);
      setDuplicateTaxList([]);
    }
  }, []);

  const checkDuplicateTax = async (tax_id) => {
    try {
      const res = await fetch(`${api_path}/customer/check_duplicate_name.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tax_id }),
      });
      const data = await res.json();
      setIsDuplicateTax(data.duplicate);
      setDuplicateTaxList(data.data || []);
    } catch (error) {
      console.error("Error checking duplicate:", error);
    }
  };

  // ── Customer Name (TH) duplicate check ──
  const checkDuplicateName = async (name) => {
    try {
      const res = await fetch(`${api_path}/customer/check_duplicate_customer_fc.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      setIsDuplicateName(data.duplicate);
      setDuplicateNameList(data.data || []);
    } catch (error) {
      console.error("Error checking duplicate name:", error);
    }
  };

  const handleChangeNameTH = useCallback((e) => {
    const value = e.target.value;
    handleChange(e); // ใช้ logic เดิมในการ set form
    clearTimeout(debounceNameTimer.current);
    if (value.trim().length > 2) {
      debounceNameTimer.current = setTimeout(() => checkDuplicateName(value), 500);
    } else {
      setIsDuplicateName(false);
      setDuplicateNameList([]);
    }
  }, [handleChange]);

  // ── File handlers ──
  const handleFileChange = useCallback((e) => {
    const { name, files: selectedFiles } = e.target;
    setFiles((prev) => ({ ...prev, [name]: [...prev[name], ...Array.from(selectedFiles)] }));
  }, []);

  const handleRemoveFile = async (docType, index) => {
    const file = files[docType][index];
    if (!window.confirm("Do you want to delete this file?")) return;
    if (file.id) {
      try {
        await fetch(`${api_path}/customer/delete-customer-file.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: file.id, file_path: file.file_path }),
        });
      } catch (error) {
        console.error("Failed to delete file", error);
      }
    }
    setFiles((prev) => {
      const updated = [...prev[docType]];
      updated.splice(index, 1);
      return { ...prev, [docType]: updated };
    });
  };

  const FILE_BASE_URL = `${api_path}/`;
  const getFileUrl = (file) => FILE_BASE_URL + file.file_path;

  const renderUpload = (name, label, required = false) => (
    <Grid
      item xs={12}
      ref={makeRef(name)}
      sx={{
        border: "1px dashed",
        borderColor: errors[name] ? "#d32f2f" : "#ccc",
        borderRadius: 2,
        p: 2, mb: 2,
        bgcolor: errors[name] ? "#fff5f5" : "#fff",
      }}
    >
      <Typography variant="h6">
        {label}
        {required && <span style={{ color: "red" }}>*</span>}
      </Typography>
      <input type="file" multiple name={name} onChange={handleFileChange} style={{ margin: "10px 0" }} />
      {files[name].map((file, index) => (
        <Box key={index} display="flex" alignItems="center" gap={1}>
          <a href={getFileUrl(file)} target="_blank" rel="noopener noreferrer"
            style={{ color: "#1976d2", textDecoration: "underline" }}>
            {file.name || file.file_name}
          </a>
          <Button size="small" color="error" onClick={() => handleRemoveFile(name, index)}>ลบ</Button>
        </Box>
      ))}
      {errors[name] && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>{errors[name]}</Typography>
      )}
    </Grid>
  );

  // ── Validation ──
  const isNum = (v) => v !== "" && !isNaN(v) && !isNaN(parseFloat(v));

  const validate = () => {
    const e = {};

    if (!form.requestDate) e.requestDate = "Please select a date";
    if (!form.requestType) e.requestType = "Please select a request type";
    if (!form.paymentType) e.paymentType = "Please select a payment type";
    if (!form.channel) e.channel = "Please select a Channel";
    if (!form.branch) e.branch = "Please enter a Branch";

    if (!form.nameTH) e.nameTH = "Please enter Thai name";
    if (!form.nameEN) e.nameEN = "Please enter English name";
    if (!form.address1) e.address1 = "Please enter address 1";
    if (!form.address2) e.address2 = "Please enter address 2";

    // ── Address Dropdown validation ──
    if (!form.city) e.city = "Please select province";
    if (!form.district) e.district = "Please select district";
    if (!form.subdistrict) e.subdistrict = "Please select subdistrict";
    if (!form.postalCode) {
      e.postalCode = "Please enter postal code";
    } else if (!/^\d{5}$/.test(form.postalCode)) {
      e.postalCode = "Postal code must be a 5-digit number";
    }

    // if (!form.country) e.country = "Please enter Country";
    if (!form.taxClassification) e.taxClassification = "Please select Tax Classification";
    if (!form.tel) e.tel = "Please enter phone number";
    if (!form.contactPerson) e.contactPerson = "Please enter Contact Person";
    if (!form.contactFinance) e.contactFinance = "Please enter Contact Person (Finance)";
    if (!form.businessType) e.businessType = "Please enter Business Type";
    if (!form.registerDate) e.registerDate = "Please enter Registration Date";

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Invalid email format";
    if (!form.taxId)
      e.taxId = "Please enter Tax ID";
    else if (!/^[0-9]{13}$/.test(form.taxId))
      e.taxId = "Tax ID must be a 13-digit number";

    if (!form.registeredCapital) e.registeredCapital = "Please enter registered capital";
    else if (!isNum(form.registeredCapital)) e.registeredCapital = "Please enter numbers only";

    if (!form.paidUpCapital) e.paidUpCapital = "Please enter paid-up capital";
    else if (!isNum(form.paidUpCapital)) e.paidUpCapital = "Please enter numbers only";

    if (!form.reportYear) e.reportYear = "Please enter report year";
    else if (!isNum(form.reportYear)) e.reportYear = "Please enter numbers only";
    if (!form.netSales) e.netSales = "Please enter net sales ";
    else if (!isNum(form.netSales)) e.netSales = "Please enter numbers only";
    if (!form.netProfit) e.netProfit = "Please enter net profit";
    else if (!isNum(form.netProfit)) e.netProfit = "Please enter numbers only";
    if (!form.fixedAssets) e.fixedAssets = "Please enter fixed assets";
    else if (!isNum(form.fixedAssets)) e.fixedAssets = "Please enter numbers only";
    if (!form.currentAssets) e.currentAssets = "Please enter current assets";
    else if (!isNum(form.currentAssets)) e.currentAssets = "Please enter numbers only";
    if (!form.totalLiabilities) e.totalLiabilities = "Please enter total liabilities";
    else if (!isNum(form.totalLiabilities)) e.totalLiabilities = "Please enter numbers only";
    if (!form.shareholderEquity) e.shareholderEquity = "Please enter shareholder equity";
    else if (!isNum(form.shareholderEquity)) e.shareholderEquity = "Please enter numbers only";

    if (!form.creditMonthlySales) e.creditMonthlySales = "Please enter estimated monthly sales";
    else if (!isNum(form.creditMonthlySales)) e.creditMonthlySales = "Please enter numbers only";
    if (!form.creditLimitRequest) e.creditLimitRequest = "Please enter credit limit";
    else if (!isNum(form.creditLimitRequest)) e.creditLimitRequest = "Please enter numbers only";

    if (form.paymentType === "credit") {
      if (!form.creditTermRequest) e.creditTermRequest = "Please enter credit term";
      else if (!isNum(form.creditTermRequest)) e.creditTermRequest = "Please enter numbers only";
    }

    if (files.doc_company_cert.length === 0)
      e.doc_company_cert = "Please attach company certificate";

    setErrors(e);
    return { isValid: Object.keys(e).length === 0, errors: e };
  };

  const scrollToError = (errorsObj) => {
    const firstKey = Object.keys(errorsObj)[0];
    if (firstKey && fieldRefs.current[firstKey]) {
      fieldRefs.current[firstKey].scrollIntoView({ behavior: "smooth", block: "center" });
      fieldRefs.current[firstKey].focus?.();
    }
  };

  const buildPayload = () => ({
    requestInfo: {
      id: job_id || null,
      userId: JSON.parse(localStorage.getItem("USER_ID") || "null"),
      requestDate: form.requestDate,
      customerCode: form.customerCode,
      requestType: form.requestType,
      oldCustomerCode: form.oldCustomerCode,
      paymentType: form.paymentType,
      channel: form.channel,
      agentName: form.agentName,
      otherType: form.otherType,
    },
    customerInfo: {
      nameTH: form.nameTH,
      nameEN: form.nameEN,
      branch: form.branch,
      address1: form.address1,
      address2: form.address2,
      address3: form.address3,
      address4: form.address4,
      subdistrict: form.subdistrict,
      district: form.district,
      city: form.city,
      postalCode: form.postalCode,
      country: form.country,
      taxClassification: form.taxClassification,
      tel: form.tel,
      ext: form.ext,
      fax: form.fax,
      mobile: form.mobile,
      email: form.email,
      contactPerson: form.contactPerson,
      contactFinance: form.contactFinance,
      financeTel: form.financeTel,
      financeExt: form.financeExt,
      taxId: form.taxId,
      businessType: form.businessType,
      registerDate: form.registerDate,
      registeredCapital: form.registeredCapital,
      paidUpCapital: form.paidUpCapital,
    },
    accountingInfo: {
      reportYear: form.reportYear,
      netSales: form.netSales,
      netProfit: form.netProfit,
      fixedAssets: form.fixedAssets,
      currentAssets: form.currentAssets,
      totalLiabilities: form.totalLiabilities,
      shareholderEquity: form.shareholderEquity,
    },
    creditRequest: {
      creditMonthlySales: form.creditMonthlySales,
      creditLimitRequest: form.creditLimitRequest,
      creditTermRequest: form.creditTermRequest,
      currency: form.currency,
    },
    paymentCondition: {
      creditCountFrom: form.creditCountFrom,
      paymentMethod: form.paymentMethod,
      paymentDay: form.paymentDay,
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { isValid, errors } = validate();
    if (!isValid) {
      alert("Please fill in all required fields correctly");
      scrollToError(errors);
      return;
    }
    try {
      const formData = new FormData();
      formData.append("data", JSON.stringify(buildPayload()));
      Object.keys(files).forEach((key) => {
        files[key].forEach((file) => formData.append(key, file));
      });
      const response = await fetch(`${api_path}/customer/customersRegistration.php`, {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (response.ok && result.status) {
        swal("Done.", "Save data successfully", "success", { buttons: true, timer: 5000 })
          .then(() => { window.location = `./CustomerRegisterDetail?id=${result.id}&type=1`; });
      } else {
        alert("เกิดข้อผิดพลาด: " + result.message);
      }
    } catch (error) {
      console.error("API Error:", error);
      alert("ไม่สามารถเชื่อมต่อ API ได้");
    }
  };
  const handleBusinessTypeChange = useCallback((selectedOption) => {
    setForm((prev) => ({
      ...prev,
      businessType: selectedOption ? selectedOption.value : "",
    }));
  }, [setForm]);
  // ─────────────────────────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <label className="head">Account Registration Form (Local customer)</label>
          </Grid>
        </Grid>
        <br />

        {/* ── Request Info ── */}
        <Grid container spacing={2}>
          <F name="requestDate" label="Request Date" required xs={4} type="date"
            value={form.requestDate} error={errors.requestDate} helperText={errors.requestDate}
            onChange={handleChange} fieldRef={makeRef("requestDate")} />
          <F name="customerCode" label="Customer Code" xs={4}
            value={form.customerCode} onChange={handleChange} fieldRef={makeRef("customerCode")} disabled={form.approval_status < 6} />
          <F name="branch" label="Branch" required xs={4}
            value={form.branch} error={errors.branch} helperText={errors.branch}
            onChange={handleChange} fieldRef={makeRef("branch")} />
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={9}>
            <Typography variant="h6" sx={{ mt: 1 }}>Request Type <span style={{ color: "red" }}>*</span></Typography>
            <RadioGroup row name="requestType" value={form.requestType} onChange={handleChange}>
              <FormControlLabel value="new" control={<Radio />} label="New Customer" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
              <FormControlLabel value="increase" control={<Radio />} label="Increase Credit Limit" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
            </RadioGroup>
            {errors.requestType && <Typography color="error" variant="body2">{errors.requestType}</Typography>}
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Typography variant="h6" sx={{ mt: 1 }}>Payment Type <span style={{ color: "red" }}>*</span></Typography>
            <RadioGroup row name="paymentType" value={form.paymentType} onChange={handleChange}>
              <FormControlLabel value="cash" control={<Radio />} label="CASH" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
              <FormControlLabel value="credit" control={<Radio />} label="CREDIT" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
            </RadioGroup>
            {errors.paymentType && <Typography color="error" variant="body2">{errors.paymentType}</Typography>}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" sx={{ mt: 1 }}>Distribution Channel <span style={{ color: "red" }}>*</span></Typography>
            <RadioGroup row name="channel" value={form.channel} onChange={handleChange}>
              <FormControlLabel value="PP" control={<Radio />} label="PP" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
              <FormControlLabel value="CC" control={<Radio />} label="CC" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
              <FormControlLabel value="PC" control={<Radio />} label="PC/CP" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
            </RadioGroup>
            {errors.channel && <Typography color="error" variant="body2">{errors.channel}</Typography>}
          </Grid>
          <F name="agentName" label="Agent Name" xs={6}
            value={form.agentName} onChange={handleChange} fieldRef={makeRef("agentName")} />
          <F name="otherType" label="Others" xs={6}
            value={form.otherType} onChange={handleChange} fieldRef={makeRef("otherType")} />
        </Grid>

        {/* ── ข้อมูลทั่วไป ── */}
        <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>General Information</Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={6} ref={makeRef("taxId")}>
            <TextField
              label="Tax ID *"
              name="taxId"
              value={form.taxId}
              onChange={handleChangeTaxid}
              fullWidth
              error={!!errors.taxId}
              helperText={errors.taxId}
              inputProps={{ maxLength: 13, inputMode: "numeric", pattern: "[0-9]*" }}
            />
            {isDuplicateTax && (
              <Box sx={{ mt: 0.5 }}>
                <Typography color="error" variant="body2">
                  ⚠️ This Tax ID may already exist in the system
                </Typography>
                {duplicateTaxList.slice(0, 5).map((item, idx) => (
                  <Typography key={idx} variant="caption" sx={{ display: "block", color: "gray", ml: 1 }}>
                    • {item.CUSTOMER_NAME || item.name}
                    {item.TAX_ID || item.tax_id ? ` — Tax ID: ${item.TAX_ID || item.tax_id}` : ""}
                  </Typography>
                ))}
              </Box>
            )}
            {form.taxId.length > 0 && form.taxId.length < 13 && (
              <Typography color="warning.main">Tax ID must be 13 digits</Typography>
            )}

          </Grid>
          <Grid item xs={6}> </Grid>

          <Grid item xs={6} ref={makeRef("nameTH")}>
            <TextField
              label="Customer Name (Thai) *"
              name="nameTH"
              value={form.nameTH}
              onChange={handleChangeNameTH}
              fullWidth
              error={!!errors.nameTH}
              helperText={errors.nameTH}
            />
            {isDuplicateName && (
              <Box sx={{ mt: 0.5 }}>
                <Typography color="error" variant="body2">
                  ⚠️ Duplicate customer name found in the system
                </Typography>
                {duplicateNameList.slice(0, 5).map((item, idx) => (
                  <Typography key={idx} variant="caption" sx={{ display: "block", color: "gray", ml: 1 }}>
                    • {item.CUSTOMER_NAME || item.name} ({item.Similarity}% similar)
                    {item.TAX_ID || item.tax_id ? ` — Tax ID: ${item.TAX_ID || item.tax_id}` : ""}
                  </Typography>
                ))}
              </Box>
            )}
          </Grid>
          <Grid item xs={6}>
            <F name="nameEN" label="Customer Name (English)" required xs={12}
              value={form.nameEN} error={errors.nameEN} helperText={errors.nameEN}
              onChange={handleChange} fieldRef={makeRef("nameEN")} />
          </Grid>

          <F name="address1" label="Address 1" required xs={12} multiline rows={2}
            value={form.address1} error={errors.address1} helperText={errors.address1}
            onChange={handleChange} fieldRef={makeRef("address1")} />
          <F name="address2" label="Address 2" required xs={12} multiline rows={2}
            value={form.address2} error={errors.address2} helperText={errors.address2}
            onChange={handleChange} fieldRef={makeRef("address2")} />
          <F name="address3" label="Address 3" xs={12} multiline rows={2}
            value={form.address3} onChange={handleChange} fieldRef={makeRef("address3")} />
          <F name="address4" label="Address 4" xs={12} multiline rows={2}
            value={form.address4} onChange={handleChange} fieldRef={makeRef("address4")} />

          {/* ── Thai Address Cascade Dropdowns ── */}
          <ThaiAddressSection
            form={form}
            errors={errors}
            makeRef={makeRef}
            provinces={provinces}
            amphoes={amphoes}
            tambons={tambons}
            handleProvinceChange={handleProvinceChange}
            handleAmphoeChange={handleAmphoeChange}
            handleTambonChange={handleTambonChange}
          />

          {/* Country (disabled) */}
          <F name="country" label="Country" required xs={6} disabled
            value={form.country} error={errors.country} helperText={errors.country}
            onChange={handleChange} fieldRef={makeRef("country")} />
          <F name="taxClassification" label="Tax Classification" xs={6}
            value={form.taxClassification} error={errors.taxClassification} helperText={errors.taxClassification}
            onChange={handleChange} fieldRef={makeRef("taxClassification")} />
          <F name="tel" label="Phone" required xs={4}
            value={form.tel} error={errors.tel} helperText={errors.tel}
            onChange={handleChange} fieldRef={makeRef("tel")} />
          <F name="ext" label="Extension" xs={2}
            value={form.ext} onChange={handleChange} fieldRef={makeRef("ext")} />
          <F name="fax" label="Fax" xs={3}
            value={form.fax} onChange={handleChange} fieldRef={makeRef("fax")} />
          <F name="mobile" label="Mobile (Optional)" xs={3}
            value={form.mobile} error={errors.mobile} helperText={errors.mobile}
            onChange={handleChange} fieldRef={makeRef("mobile")} />

          <F name="email" label="Email (Optional)" xs={6}
            value={form.email} error={errors.email} helperText={errors.email}
            onChange={handleChange} fieldRef={makeRef("email")} />
          <F name="contactPerson" label="Contact Person" required xs={6}
            value={form.contactPerson} error={errors.contactPerson} helperText={errors.contactPerson}
            onChange={handleChange} fieldRef={makeRef("contactPerson")} />
          <F name="contactFinance" label="Contact Person (Finance/Accounting)" required xs={6}
            value={form.contactFinance} error={errors.contactFinance} helperText={errors.contactFinance}
            onChange={handleChange} fieldRef={makeRef("contactFinance")} />

          <F name="financeTel" label="Phone (Finance)" xs={4}
            value={form.financeTel} onChange={handleChange} fieldRef={makeRef("financeTel")} />
          <F name="financeExt" label="Extension" xs={2}
            value={form.financeExt} onChange={handleChange} fieldRef={makeRef("financeExt")} />



          <Grid item xs={6} ref={makeRef("businessType")} sx={{ mb: 4 }}>
            {/* <Typography
              variant="caption"
              sx={{ display: "block", mb: 0.5, color: errors.businessType ? "#d32f2f" : "rgba(0,0,0,0.6)" }}
            >
              ประเภทธุรกิจ *
            </Typography> */}
            <Select
              placeholder="Select Business Type..."
              value={
                businessTypeOptions.find((opt) => opt.value === form.businessType) || null
              }
              options={businessTypeOptions}
              onChange={handleBusinessTypeChange}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
              isClearable
              isDisabled={loadingBusinessType}
              isLoading={loadingBusinessType}
              menuPosition="fixed"
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: errors.businessType ? "#d32f2f" : base.borderColor,
                  minHeight: "47px", // ให้สูงเท่ากับ MUI TextField (size=medium)
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999, // ยกขึ้นให้ลอยอยู่บนสุด ไม่ถูกซ้อนทับ
                }),
              }}
            />
            {errors.businessType && (
              <Typography color="error" variant="body2" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.businessType}
              </Typography>
            )}


          </Grid>
          {/* <F name="businessType" label="ประเภทธุรกิจ" required xs={6}
            value={form.businessType} error={errors.businessType} helperText={errors.businessType}
            onChange={handleChange} fieldRef={makeRef("businessType")} /> */}
          <F name="registerDate" label="Registration Date" required xs={6} type="date"
            value={form.registerDate} error={errors.registerDate} helperText={errors.registerDate}
            onChange={handleChange} fieldRef={makeRef("registerDate")} />
          <F name="registeredCapital" label="Registered Capital (THB)" required xs={6} type="number"
            value={form.registeredCapital} error={errors.registeredCapital} helperText={errors.registeredCapital}
            onChange={handleChange} fieldRef={makeRef("registeredCapital")} />
          <F name="paidUpCapital" label="Paid-up Capital (THB)" required xs={6} type="number"
            value={form.paidUpCapital} error={errors.paidUpCapital} helperText={errors.paidUpCapital}
            onChange={handleChange} fieldRef={makeRef("paidUpCapital")} />
        </Grid>

        <Typography variant="body2" sx={{ mt: 2, color: "gray" }}>
          **For general customer account opening cases that are not "Freight" and do not have credit terms (Cash)
          The form is specifically designed for general information sections without requiring credit application documents
        </Typography>

        {/* ── ข้อมูลด้านบัญชี ── */}
        <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>Account Information</Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <F name="reportYear" label="Report Year" required xs={4} type="number"
            value={form.reportYear} error={errors.reportYear} helperText={errors.reportYear}
            onChange={handleChange} fieldRef={makeRef("reportYear")} />
          <F name="netSales" label="Net Sales (THB)" required xs={4} type="number"
            value={form.netSales} error={errors.netSales} helperText={errors.netSales}
            onChange={handleChange} fieldRef={makeRef("netSales")} />
          <F name="netProfit" label="Net Profit (THB)" required xs={4} type="number"
            value={form.netProfit} error={errors.netProfit} helperText={errors.netProfit}
            onChange={handleChange} fieldRef={makeRef("netProfit")} />
          <F name="fixedAssets" label="Fixed Assets (THB)" required xs={4} type="number"
            value={form.fixedAssets} error={errors.fixedAssets} helperText={errors.fixedAssets}
            onChange={handleChange} fieldRef={makeRef("fixedAssets")} />
          <F name="currentAssets" label="Current Assets (THB)" required xs={4} type="number"
            value={form.currentAssets} error={errors.currentAssets} helperText={errors.currentAssets}
            onChange={handleChange} fieldRef={makeRef("currentAssets")} />
          <F name="totalLiabilities" label="Total Liabilities (THB)" required xs={4} type="number"
            value={form.totalLiabilities} error={errors.totalLiabilities} helperText={errors.totalLiabilities}
            onChange={handleChange} fieldRef={makeRef("totalLiabilities")} />
          <F name="shareholderEquity" label="Shareholder Equity (THB)" required xs={4} type="number"
            value={form.shareholderEquity} error={errors.shareholderEquity} helperText={errors.shareholderEquity}
            onChange={handleChange} fieldRef={makeRef("shareholderEquity")} />
        </Grid>

        {/* ── วงเงินเครดิต ── */}
        <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>Credit Limit Request</Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <F name="creditMonthlySales" label="Estimated Monthly Sales (THB)" required xs={4} type="number"
            value={form.creditMonthlySales} error={errors.creditMonthlySales} helperText={errors.creditMonthlySales}
            onChange={handleChange} fieldRef={makeRef("creditMonthlySales")} />
          <F name="creditLimitRequest" label="Credit Limit Request (THB)" required xs={4} type="number"
            value={form.creditLimitRequest} error={errors.creditLimitRequest} helperText={errors.creditLimitRequest}
            onChange={handleChange} fieldRef={makeRef("creditLimitRequest")} />
          <F name="creditTermRequest"
            label={`Credit Term Request${form.paymentType === "credit" ? " *" : ""}`}
            xs={4} type="number"
            value={form.creditTermRequest} error={errors.creditTermRequest} helperText={errors.creditTermRequest}
            onChange={handleChange} fieldRef={makeRef("creditTermRequest")} />
          <Grid item xs={4}>
            <TextField label="Currency" value={form.currency} fullWidth disabled />
          </Grid>
        </Grid>

        <Typography variant="body2" sx={{ mt: 2, color: "gray" }}>
          Credit limit shall not exceed the registered capital of the company and the applicant.
        </Typography>

        {/* ── เงื่อนไขการชำระ ── */}
        <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>Payment Terms</Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>Credit Counting Method</FormLabel>
              <RadioGroup name="creditCountFrom" value={form.creditCountFrom} onChange={handleChange}
                sx={{
                  display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 1,
                  "& .MuiFormControlLabel-root": { alignItems: "center" },
                  "& .MuiFormControlLabel-label": { whiteSpace: "nowrap" },
                }}>
                <FormControlLabel value="invoice_date" control={<Radio />} label="Count from Invoice Date" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
                <FormControlLabel value="billing_date" control={<Radio />} label="Count from Billing Date" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
                <FormControlLabel value="other" control={<Radio />} label="Other" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>Payment Schedule</FormLabel>
              <RadioGroup row name="paymentMethod" value={form.paymentMethod} onChange={handleChange}
                sx={{
                  display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1, mt: 1,
                  "& .MuiFormControlLabel-label": { whiteSpace: "nowrap" },
                }}>
                <FormControlLabel value="cheque" control={<Radio />} label="Pay by Check" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
                <FormControlLabel value="transfer" control={<Radio />} label="Transfer to Bank Account" componentsProps={{ typography: { fontSize: "0.8rem" } }} />
              </RadioGroup>
            </FormControl>
          </Grid>
          <F name="paymentDay" label="กำหนดการจ่ายเช็ค / โอนเงิน ทุกวันที่" xs={6}
            value={form.paymentDay} onChange={handleChange} fieldRef={makeRef("paymentDay")} />
        </Grid>

        {/* ── เอกสาร ── */}
        <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>Supporting Documents for Credit Application</Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {renderUpload("doc_company_cert", "Company Certificate (not more than 3 months old)", true)}
            {renderUpload("doc_financial_statement", "Financial Statements / Profit & Loss / DBD")}
            {renderUpload("doc_vat20", "Copy of VAT Form 20")}
            {renderUpload("doc_delivery_schedule", "Delivery Schedule / Billing / Check Payment")}
            {renderUpload("doc_company_map", "Company Map / Check Receipt Map")}
            {renderUpload("doc_company_photo", "Photo of Business Location")}
          </Grid>
        </Grid>

        <Grid container sx={{ mt: 4 }}>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" type="submit">Submit</Button>
          </Grid>
        </Grid>
      </Box>
    </form>
  );
}
